import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { Button, Dropdown } from "antd";
import { Edit2, More, Trash } from "iconsax-react";
import type { KeyboardEvent, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { CustomCard } from "src/components/ui/Card";
import { cn } from "src/util/cn";
import type {
  WarehouseResource,
  WarehouseResourceType,
} from "src/services/warehouseService";
import { hierarchyConfig } from "../hierarchyConfig";
import { isResourceActive } from "../warehouseHierarchy.util";
import {
  CARD_DOT_ICON,
  WAREHOUSE_AREA_ICON,
  WAREHOUSE_ICON,
  WAREHOUSE_LANE_ICON,
} from "src/assets/svgs";
import { CustomTypography } from "../../../../sipli-fleet-core/components";


interface WarehouseResourceCardProps {
  resource: WarehouseResourceType;
  record: WarehouseResource;
  statusLoading?: boolean;
  onOpen?: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (active: boolean) => void;
}

const childCountKeys: Record<WarehouseResourceType, string[]> = {
  warehouse: ["areaCount", "areasCount", "numberOfAreas", "totalAreas"],
  area: ["laneCount", "lanesCount", "numberOfLanes", "totalLanes"],
  lane: ["shelfCount", "shelvesCount", "numberOfShelves", "totalShelves"],
  shelf: [
    "positionCount",
    "positionsCount",
    "numberOfPositions",
    "totalPositions",
  ],
  position: [],
};

const getChildCount = (
  resource: WarehouseResourceType,
  record: WarehouseResource,
) =>
  childCountKeys[resource].reduce<number | undefined>((count, key) => {
    if (count !== undefined) {
      return count;
    }

    const value = record[key];
    return typeof value === "number" ? value : undefined;
  }, undefined);

const countSummaryConfig: Record<
  WarehouseResourceType,
  Array<{ keys: string[]; label: string }>
> = {
  warehouse: [
    { keys: ["totalAreas"], label: "areas" },
    // { keys: ["totalLanes"], label: "lanes" },
    // { keys: ["totalShelfs", "totalShelves"], label: "shelves" },
    // { keys: ["totalPositions"], label: "positions" },
  ],
  area: [
    { keys: ["totalLanes"], label: "lanes" },
    // { keys: ["totalShelfs", "totalShelves"], label: "shelves" },
    // { keys: ["totalPositions"], label: "positions" },
  ],
  lane: [
    { keys: ["totalShelfs", "totalShelves"], label: "shelves" },
    // { keys: ["totalPositions"], label: "positions" },
  ],
  shelf: [{ keys: ["totalPositions"], label: "positions" }],
  position: [],
};

const WarehouseResourceCard = ({
  resource,
  record,
  statusLoading = false,
  onOpen,
  onEdit,
  onDelete,
  onStatusChange,
}: WarehouseResourceCardProps) => {
  const { t, i18n } = useTranslation();
  const config = hierarchyConfig[resource];
  const active = isResourceActive(record);
  const childCount = getChildCount(resource, record);
  const countSummary = countSummaryConfig[resource]
    .map(({ keys, label }) => {
      const value = keys
        .map((key) => record[key])
        .find((metric): metric is number => typeof metric === "number");
      return value && value > 0 ? `${formatNumber(value, i18n.language)} ${t(label)}` : null;
    })
    .filter(Boolean);
  const isNavigable = Boolean(onOpen);

  const stopPropagation = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
  };

  const subtitle =
    childCount !== undefined && config.childPluralKey
      ? `${childCount} ${t(config.childPluralKey)}`
      : record.code ||
        record.warehouseType?.name ||
        record.warehouseTypeName ||
        t(config.singularKey);

  const iconSrcMap = {
    "area": WAREHOUSE_AREA_ICON,
    "lane": WAREHOUSE_LANE_ICON,
    "shelf": WAREHOUSE_LANE_ICON,
    "position": WAREHOUSE_LANE_ICON,
  }
        
  const altText =
    resource === "area"
      ? "warehouse-area-icon"
      : resource === "lane"
      ? "warehouse-lane-icon"
      : "warehouse-icon";

  return (
    <CustomCard
      hoverable={isNavigable}
      role={isNavigable ? "button" : undefined}
      tabIndex={isNavigable ? 0 : undefined}
      aria-label={
        isNavigable
          ? t("open_resource", {
              resource: t(config.singularKey),
              name: record.name ?? record.id,
            })
          : undefined
      }
      className="warehouse-resource-card"
      styles={{ body: { padding: 0, height: "100%" } }}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (onOpen && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="flex min-h-[156px] h-full flex-col gap-[8px] p-[16px]">
        <div className="flex items-center justify-between">
          <span
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-full",
              config.accentClassName,
            )}
          >
            <img src={iconSrcMap[resource] || WAREHOUSE_ICON} alt={altText} />
          </span>
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="warehouse-action-dropdown"
            popupRender={() => (
              <div
                className="min-w-[132px] rounded-[6px] bg-white p-1 shadow-[0_8px_24px_rgba(15,16,21,0.14)]"
                onClick={stopPropagation}
                onKeyDown={stopPropagation}
              >
                <Button
                  type="text"
                  className="!flex !items-center !justify-start !cursor-pointer !h-[36px] !w-full !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!outline-none"
                  onClick={onEdit}
                >
                  {t("edit")}
                </Button>
                <Button
                  type="text"
                  loading={statusLoading}
                  className="!flex !items-center !justify-start !cursor-pointer !h-[36px] !w-full !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!outline-none"
                  onClick={() => onStatusChange(!active)}
                >
                  {active ? t("inactive") : t("active")}
                </Button>
                <Button
                  type="text"
                  className="!flex !items-center !justify-start !cursor-pointer !h-[36px] !w-full !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-red hover:!bg-primary6 focus:!outline-none"
                  onClick={onDelete}
                >
                  {t("delete")}
                </Button>
              </div>
            )}
          > 
            <img src={CARD_DOT_ICON} alt="card-dot-icon" onClick={stopPropagation} className="cursor-pointer" />
          </Dropdown>
        </div>

        <div className="flex flex-col gap-1">
          <CustomTypography.CustomText className="!truncate !text-[17.72px] !font-[700] !leading-[28px] text-gray12">
            {record.name || "--"}
          </CustomTypography.CustomText>
          {countSummary.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-1">
              {countSummary.map((summaryText) => (
                <CustomTypography.CustomText
                  key={summaryText}
                  className="!truncate !text-[14px] !leading-[18px] !font-[400] !text-gray8"
                >
                  {summaryText}
                </CustomTypography.CustomText>
              ))}
            </div>
          ) : (
            <CustomTypography.CustomText className="!truncate !text-[14px] !leading-[18px] !font-[400] !text-gray8">
              --
            </CustomTypography.CustomText>
          )}
        </div>

        

        <div
          aria-hidden
          className={cn(
            "flex items-center gap-2 mt-2",
          )}
        >
          <span
            className={`h-[8px] w-[8px] rounded-full ${
              active ? "bg-green" : "bg-gray7"
            }`}
          />
          <CustomTypography.CustomText className="!leading-[18px]">
            {t(active ? "active" : "inactive")}
          </CustomTypography.CustomText>
        </div>
      </div>
    </CustomCard>
  );
};

export default WarehouseResourceCard;
